import { Avatar, AvatarFallback, AvatarImage, Badge } from "@devhop/ui";
import { useEffect, useRef } from "react";
import { useDrop } from "react-dnd";
import type { UserWithAssignmentDTO } from "@/modules/assign-position/domain/contracts";
import { formatCurrentSalaryGradeLine } from "@/modules/assign-position/presentation/utils/format-current-salary-grade-line";
import type { PositionDTO } from "@/modules/positions/domain/contracts";
import { config } from "@/shared/lib/config";
import { getInitials } from "@/shared/lib/utils";

type DroppablePositionCardProps = {
  position: PositionDTO & {
    assignedEmployees: UserWithAssignmentDTO[];
  };
  level: number;
  canManage: boolean;
  onDrop: (userId: string, positionId: string) => void;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

export function DroppablePositionCard({
  position,
  level,
  canManage,
  onDrop,
  isHovered,
  onMouseEnter,
  onMouseLeave,
}: DroppablePositionCardProps) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "employee",
    drop: (item: { userId: string }) => {
      if (item.userId && item.userId !== position.id) {
        onDrop(item.userId, position.id);
      }
    },
    canDrop: () => canManage,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (canManage && divRef.current) {
      drop(divRef.current);
    }
  }, [drop, canManage]);

  const isDraggedOver = isOver && canDrop;
  const isAtCapacity = position.maxEmployees
    ? position.assignedEmployees.length >= position.maxEmployees
    : false;

  return (
    <section
      ref={divRef}
      aria-label={`Position: ${position.title}`}
      className={`relative mb-4 rounded-lg border bg-card transition-all ${
        isDraggedOver
          ? "border-primary bg-primary/5 shadow-lg ring-2 ring-primary"
          : isHovered
            ? "border-border shadow-md"
            : "border-border"
      } ${!position.status ? "opacity-60" : ""}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ marginLeft: `${level * 24}px` }}
    >
      {/* Level indicator */}
      <div
        className="absolute top-0 left-0 h-full w-1 rounded-l-lg bg-foreground/10"
        style={{
          opacity:
            position.level === 1
              ? 1
              : position.level === 2
                ? 0.7
                : position.level === 3
                  ? 0.5
                  : 0.3,
        }}
      />

      <div className="px-4 py-3 pl-5">
        {/* Position Header */}
        <div className="mb-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="font-medium text-sm">{position.title}</div>
              {position.departmentName && (
                <div className="mt-0.5 text-muted-foreground text-xs">
                  {position.departmentName}
                </div>
              )}
              {position.parentTitle && (
                <div className="mt-0.5 text-muted-foreground text-xs">
                  ລາຍງານໄປຫາ: {position.parentTitle}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant={isAtCapacity ? "outline" : "secondary"}
                className="font-normal text-xs"
              >
                {position.assignedEmployees.length}
                {position.maxEmployees && `/${position.maxEmployees}`}
              </Badge>
              {!position.status && (
                <span className="text-muted-foreground text-xs">ບໍ່ໃຊ້ງານ</span>
              )}
            </div>
          </div>
        </div>

        {/* Assigned Employees */}
        {position.assignedEmployees.length > 0 && (
          <div className="mt-2 border-border border-t pt-2">
            <div className="flex flex-wrap gap-2">
              {position.assignedEmployees.map((emp) => {
                const gradeLine = formatCurrentSalaryGradeLine(emp);
                return (
                  <div
                    key={emp.id}
                    className="flex flex-col gap-0.5 rounded-md border border-border bg-background px-2 py-1"
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage
                          src={
                            emp.image ? config.apiUrl + emp.image : undefined
                          }
                          alt={emp.name ?? "avatar"}
                        />
                        <AvatarFallback className="text-xs">
                          {getInitials(emp.name ?? emp.email)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs">{emp.name ?? emp.email}</span>
                    </div>
                    {gradeLine ? (
                      <span className="pl-8 text-muted-foreground text-[11px] leading-tight">
                        ລະດັບ: {gradeLine}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Drop Zone Indicator */}
        {isDraggedOver && canManage && (
          <div className="mt-2 rounded border-2 border-primary border-dashed bg-primary/5 p-2 text-center">
            <span className="text-primary text-xs">ວາງພະນັກງານທີ່ນີ້</span>
          </div>
        )}
      </div>
    </section>
  );
}
